import { useIntl } from "react-intl";
import dayjs from "dayjs";
import { Activity, AgencyDetails } from "@/interfaces/agencyDetails";
import { Section } from "@/components/common/section/Section";
import { StaticMap } from "@/components/common/staticMap/StaticMap";
import { getGoogleMapsAgencyLink } from "@/components/pages/reservation/categories/agencyOption/agencyInformationPopover/AgencyInformationPopover";
import { AdditionalInformation } from "./additionalInformation/AdditionalInformation";
import styles from "./AgencyInformation.module.scss";

type AgencyInformationProps = {
  agency: AgencyDetails;
  isOnlyWithoutLicence: boolean;
};

export const AgencyInformation = ({
  agency,
  isOnlyWithoutLicence,
}: AgencyInformationProps) => {
  const intl = useIntl();

  const activities = agency.activities.filter(
    (activity) => activity !== Activity.ROUND_TRIP
  );

  let servicesInfoContent = "";
  activities.forEach((activity, index) => {
    const isLastActivity = index === activities.length - 1;
    servicesInfoContent = `${servicesInfoContent}${intl.formatMessage({
      id: activity,
    })}${!isLastActivity ? ", " : ""}`;
  });

  type Information = {
    title: string;
    content: string;
  };

  const additionalInformations: Information[] = [];

  if (agency.accessibilityInfo !== null) {
    additionalInformations.push({
      title: intl.formatMessage({ id: "accessibility" }),
      content: agency.accessibilityInfo,
    });
  }

  if (servicesInfoContent !== "") {
    additionalInformations.push({
      title: intl.formatMessage({ id: "availableServices" }),
      content: servicesInfoContent,
    });
  }
  function addMinutes(date: string, minutes: number) {
    return dayjs(date, { utc: true })
      .add(minutes, "minutes")
      .format("HH:mm:ss")
      .toString();
  }

  return (
    <Section>
      <div className={styles.agencyBlock}>
        <div className={styles.locationBlock}>
          <StaticMap
            mapName={
              isOnlyWithoutLicence
                ? "agences-sans-permis-rent-a-car_828748"
                : "toutes-les-agences-rent-a-car-v2_1118492"
            }
            forceX={agency.addressDto.latitude}
            forceY={agency.addressDto.longitude}
            forceZoom={"15"}
            folder={"agencies"}
          />
        </div>
        <div className={styles.informationBlock}>
          <div className={styles.informationSection}>
            <p className={styles.informationTitle}>
              {intl.formatMessage({ id: "address" })}
            </p>
            <p className={styles.semibold}>{agency.addressDto.street}</p>
            <p>
              {agency.addressDto.zipCode} {agency.addressDto.city}
            </p>
            <div className={styles.gmapLink}>
              <a
                href={getGoogleMapsAgencyLink(agency.addressDto)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {intl.formatMessage({ id: "openInGoogleMaps" })}
              </a>
            </div>
          </div>
          <div className={styles.informationSection}>
            <p className={styles.informationTitle}>
              {intl.formatMessage({ id: "contact" })}
            </p>
            <p className={styles.contactInformation}>
              {agency.communicationMeansDto.phone !== null && (
                <a href={`tel:${agency.communicationMeansDto.phone}`}>
                  {agency.communicationMeansDto.phone}
                </a>
              )}
              {agency.communicationMeansDto.email !== null && (
                <a
                  className={styles.semibold}
                  href={`mailto:${agency.communicationMeansDto.email}`}
                >
                  {agency.communicationMeansDto.email}
                </a>
              )}
            </p>
          </div>
          <div className={styles.informationSection}>
            <p className={styles.informationTitle}>
              {intl.formatMessage({ id: "openingHours" })}
            </p>
            {agency.nextSchedules.map((schedule) => (
              <div className={styles.scheduleInformation} key={schedule.day}>
                <div className={styles.dayLabelDesktop}>
                  {dayjs(schedule.day).format("dddd DD/MM")}
                </div>
                <div className={styles.dayLabelMobile}>
                  {dayjs(schedule.day).format("ddd DD/MM")}
                </div>
                <div className={styles.dayValues}>
                  {schedule.slots.map((slot, index) => {
                    const endReal = addMinutes(
                      schedule.day.split("T")[0] + "T" + slot.end,
                      15
                    );
                    const start = slot.start.slice(0, 5).replace(":", "h");
                    const end = endReal.slice(0, 5).replace(":", "h");

                    return (
                      <div className={styles.dayValue} key={index}>
                        {index > 0 && <>&nbsp;</>}
                        {start} - {end}
                        {index < schedule.slots.length - 1 && " /"}
                      </div>
                    );
                  })}
                  {!schedule.slots.length && (
                    <div className={styles.dayValue}>
                      {intl.formatMessage({ id: "closed" })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {additionalInformations.length > 0 && (
        <div className={styles.additionalInformationBlock}>
          {additionalInformations.map((information) => (
            <AdditionalInformation
              title={information.title}
              content={information.content}
              key={information.title}
            />
          ))}
        </div>
      )}
    </Section>
  );
};
